import { db } from '../../config/firebaseConfig';
import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { firestoreConnect } from 'react-redux-firebase';
import { connect } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import 'moment/locale/fr';
import Task from './Task';
import FilterButton from '../filtering/FilterButton.jsx';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, Form, Badge } from 'react-bootstrap';

const FILTER_MAP = {
  Toute: () => true,
  Active: (task) => !task.checked,
  Terminé: (task) => task.checked,
  "Avec RDV": (task) => task.rdvDate && task.rdvDate.trim() !== "" &&
    task.rdvDate.toLowerCase().trim() !== "aucun",
  // 📝 Filtre les tâches qui n'ont pas de rendez-vous programmé
  "Sans RDV": (task) => !task.rdvDate || task.rdvDate.trim() === "" ||
    task.rdvDate.toLowerCase().trim() === "aucun"
};

const FILTER_NAMES = Object.keys(FILTER_MAP);

function Tasks({ tasks }) {
  // 1. TOUS VOS STATES EN PREMIER
  const [filter, setFilter] = useState(() => {
    return localStorage.getItem('memento_tasks_filter') || 'Active';
  });

  // Crée une horloge interne qui va stocker la date et l'heure actuelle
  const [currentTime, setCurrentTime] = useState(new Date());

  // ⏱️ État pour contrôler la visibilité des badges (visibles au début)
  const [showBadges, setShowBadges] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sentAlerts, setSentAlerts] = useState(() => {
    const sauvegardes = localStorage.getItem('memento_sent_alerts');
    return sauvegardes ? JSON.parse(sauvegardes) : [];
  });

  const [localOrderedTasks, setLocalOrderedTasks] = useState([]);

  // 1. On filtre et on trie les tâches en incluant la recherche textuelle
  const processedTasks = useMemo(() => {
    return tasks ? [...tasks]
      .filter(FILTER_MAP[filter])
      // 🔍 NOUVEAU : Filtre par la barre de recherche textuelle
      .filter((task) =>
        task.task && task.task.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        // 🎯 SI SANS RDV : On trie par le numéro d'ordre sauvegardé
        if (filter === 'Sans RDV') {
          return (a.order ?? 0) - (b.order ?? 0);
        }

        // 🎯 SI AVEC RDV : Tri chronologique ultra-précis (Date + Heure)
        const getValidMoment = (rdvDate, rdvTime) => {
          if (!rdvDate || rdvDate.toLowerCase().trim() === 'aucun') {
            return moment(0); // Retourne une date très lointaine dans le passé
          }

          // 1. On nettoie et extrait la base de la date (gère le format ISO ou le texte français)
          let baseDate;
          if (rdvDate.includes && rdvDate.includes(' ')) {
            // Si c'est du texte français comme "samedi 26 septembre 2026"
            baseDate = moment(rdvDate, 'dddd D MMMM YYYY', 'fr');
          } else {
            // Si c'est un format ISO standard (YYYY-MM-DD)
            baseDate = moment(rdvDate);
          }

          // Sécurité si la date est corrompue
          if (!baseDate.isValid()) return moment(0);

          // 2. Si une heure est spécifiée (ex: "14:00"), on la fusionne proprement
          if (rdvTime && rdvTime.trim() !== "" && rdvTime.toLowerCase() !== "heure non spécifiée") {
            const [hours, minutes] = rdvTime.split(':');
            baseDate.set({ hour: parseInt(hours, 10), minute: parseInt(minutes, 10), second: 0 });
          } else {
            // Si pas d'heure, on cale à minuit pour placer la tâche au début de la journée
            baseDate.set({ hour: 0, minute: 0, second: 0 });
          }
          return baseDate;
        };

        const momentA = getValidMoment(a.rdvDate, a.rdvTime);
        const momentB = getValidMoment(b.rdvDate, b.rdvTime);

        // Tri par ordre chronologique croissant (le plus ancien/plus tôt en premier)
        return momentA.valueOf() - momentB.valueOf();
      }) : [];
  }, [tasks, filter, searchTerm]); // 🟢 Ne se recalcule QUE si ces variables changent

  // 3. LA FONCTION DRAG & DROP (Elle a maintenant accès à processedTasks sans erreur)
  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    // 1. On travaille sur notre état local synchronisé
    const items = Array.from(localOrderedTasks.length > 0 ? localOrderedTasks : processedTasks);

    // 2. On déplace l'élément localement
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // 3. 🎯 CRUCIAL : On force l'affichage React à se mettre à jour INSTANTANÉMENT
    setLocalOrderedTasks(items);

    // 3. 🎯 SÉCURITÉ & PERSISTANCE : On met à jour le champ 'order' dans Firebase pour les éléments modifiés
    items.forEach((task, index) => {
      // On n'envoie à Firebase que si la position a réellement changé pour économiser les quotas
      if (task.order !== index) {
        db.collection('tasks')
          .doc(task.id)
          .update({
            order: index // Enregistre le nouveau numéro (0, 1, 2, 3...)
          })
          .catch((err) => console.error("Erreur sauvegarde ordre :", err));
      }
    });

    console.log("Nouvel ordre local pour Sans RDV :", items);
  };

  // 🔔 Demande l'autorisation d'envoyer des notifications au chargement
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // 💾 Sauvegarde le filtre automatiquement à chaque changement
  useEffect(() => {
    localStorage.setItem('memento_tasks_filter', filter);
  }, [filter]);


  useEffect(() => {
    if (processedTasks.length > 0) {
      setLocalOrderedTasks(processedTasks);
    }
  }, [processedTasks]);

  // À chaque fois que l'utilisateur clique sur un filtre, on réaffiche les badges pendant 5 secondes
  useEffect(() => {
    setShowBadges(true); // Réaffiche les badges instantanément au clic

    // Lance le compte à rebours de 5000 ms (5 secondes)
    const timer = setTimeout(() => {
      setShowBadges(false); // Masque les badges
    }, 5000);

    return () => clearTimeout(timer); // Nettoie le timer si l'utilisateur clique frénétiquement
  }, [filter]); // Se déclenche dès que 'filter' change

  // Met à jour cette horloge toutes les 30 secondes pour une précision parfaite
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(timer); // Nettoyage à la fermeture
  }, []);

  // 🎯 Surveillance automatique du temps pour cocher les tâches expirées
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;

    const now = currentTime;

    tasks.forEach((task) => {
      if (!task.rdvDate || task.rdvDate.toLowerCase().trim() === 'aucun') return;

      // Décodage de la date (gestion si c'est un Timestamp Firebase ou une String)
      const jsDate = task.rdvDate.toDate ? task.rdvDate.toDate() : new Date(task.rdvDate);
      if (isNaN(jsDate.getTime())) return;

      // Si la tâche a une heure précise (rdvTime), on fusionne la date et l'heure
      let rdvDateTime = jsDate;
      if (task.rdvTime) {
        rdvDateTime = moment(`${moment(jsDate).format('YYYY-MM-DD')} ${task.rdvTime}`, 'YYYY-MM-DD HH:mm').toDate();
      }

      // -------------------------------------------------------------
      // 🤖 CLÔTURE AUTOMATIQUE : Centralisée et unique
      // -------------------------------------------------------------
      const isTimeOver = now > rdvDateTime;

      if (isTimeOver && !task.checked && !task.autoClosed && !task.touchedByUser) {
        const updateData = {
          checked: true,
          autoClosed: true, // 🟢 Verrouille définitivement la tâche sur Firebase
          completedAt: new Date()
        };

        // 1. Écriture unique sur Firebase
        db.collection('tasks')
          .doc(task.id)
          .update(updateData)
          .then(() => {
            console.log(`La tâche "${task.task || "Sans nom"}" a été clôturée par le parent.`);
          })
          .catch((err) => console.error("Erreur de clôture automatique :", err));
      }
    });
  }, [currentTime, tasks]);

  // 🎯 Surveillance automatique du temps pour cocher les tâches expirées
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;

    const now = currentTime;

    tasks.forEach((task) => {
      if (!task.rdvDate || task.rdvDate.toLowerCase().trim() === 'aucun') return;

      // Décodage de la date (gestion si c'est un Timestamp Firebase ou une String)
      const jsDate = task.rdvDate.toDate ? task.rdvDate.toDate() : new Date(task.rdvDate);
      if (isNaN(jsDate.getTime())) return;

      // Si la tâche a une heure précise (rdvTime), on fusionne la date et l'heure
      let rdvDateTime = jsDate;
      if (task.rdvTime) {
        rdvDateTime = moment(`${moment(jsDate).format('YYYY-MM-DD')} ${task.rdvTime}`, 'YYYY-MM-DD HH:mm').toDate();
      }

      const differenceEnMs = rdvDateTime.getTime() - now.getTime();
      const minutesRestantes = Math.floor(differenceEnMs / 60000);

      // -------------------------------------------------------------
      // 🔔 ALERTE VISUELLE : Déclenchement à 5 minutes
      // -------------------------------------------------------------
      // 🚀 1. SÉCURITÉ CALENDRIER : On vérifie si le rendez-vous est prévu pour aujourd'hui
      const isAujourdHui = rdvDateTime.getDate() === now.getDate() &&
        rdvDateTime.getMonth() === now.getMonth() &&
        rdvDateTime.getFullYear() === now.getFullYear();

      // -------------------------------------------------------------
      // 🔔 ALERTE WINDOWS ACCRUE : Déclenchement J-1 (24 heures à l'avance)
      // -------------------------------------------------------------
      // 🚀 LA CORRECTION : En ajoutant "!isAujourdHui", l'alerte ignore les rendez-vous du jour même !
      if (minutesRestantes <= 1440 && minutesRestantes > 0 && !isAujourdHui && !task.checked && !sentAlerts.includes(task.id)) {

        // 1. Déclenchement de la notification de navigateur
        if ('Notification' in window && Notification.permission === 'granted') {
          const notification = new Notification("Rappel Memento ! ⏳", {
            body: `Votre rendez-vous pour "${task.task || 'une tâche'}" commence dans 24 heures.`,
            icon: '/logo192.png',
            requireInteraction: true
          });

          // 🎯 ACTION AU CLIC : Ramène l'utilisateur sur l'application Memento
          notification.onclick = function (event) {
            event.preventDefault(); // Empêche certains comportements par défaut du système
            window.focus();         // Force le navigateur à surélever l'onglet au premier plan
            notification.close();   // Ferme la notification après le clic
          };
        }

        // 2. 🎯 VERROU LOCAL : On enregistre dans le state que cette notification a été envoyée
        setSentAlerts((prev) => [...prev, task.id]);
        console.log(`🔔 Notification J-1 isolée et envoyée avec succès.`);
      }
    });
  }, [currentTime, tasks, sentAlerts, filter, searchTerm]);

  // 🎯 AUTOMATISME : Réactivation des tâches récurrentes tous les 5 du mois
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;

    const aujourdHui = moment(currentTime);
    const jourDuMois = aujourdHui.date(); // Renvoie le numéro du jour (1 à 31)
    const moisActuel = aujourdHui.format('MM'); // Renvoie le mois en cours (ex: "10")

    // 🚀 LA VRAIE CORRECTION AVEC LES CROCHETS ENFIN LÀ !
    const joursGeres =[5, 10, 15];

    // Si aujourd'hui est le 5, le 10 ou le 15, on traite les tâches correspondantes
    if (joursGeres.includes(jourDuMois)) {
      tasks.forEach((task) => {

        // Le type attendu en base (ex: "monthly_5", "monthly_10", "monthly_15")
        const typeAttendu = `monthly_${jourDuMois}`;

        // On cible uniquement les tâches récurrentes du 5 du mois qui sont terminées (checked)
        // et qui n'ont pas encore été réinitialisées ce mois-ci
        if (
          task.isRecurrent &&
          task.recurrenceType === typeAttendu &&
          task.checked &&
          task.lastResetMonth !== moisActuel
        ) {

          // 🔄 Calcul de la nouvelle date du rendez-vous pour le mois en cours
          const nouvelleDateRdv = aujourdHui.format(`YYYY-MM-${String(jourDuMois).padStart(2, '0')}`);

          db.collection('tasks')
            .doc(task.id)
            .update({
              checked: false,         // 🚀 Elle redevient active !
              completedAt: null,       // Efface l'ancienne date de clôture
              rdvDate: nouvelleDateRdv, // Met à jour la date du calendrier sur le 5 du mois en cours
              lastResetMonth: moisActuel // Verrouille pour ce mois-ci (évite de boucler toutes les 30s)
            })
            .then(() => {
              console.log(`🔄 Tâche récurrente réactivée pour le 5 du mois : "${task.task}"`);
            })
            .catch((err) => console.error("Erreur récurrence Firestore :", err));
        }
      });
    }
  }, [currentTime, tasks]);


  // 🚀 Dès que le tableau sentAlerts change, on l'enregistre dans le localStorage
  useEffect(() => {
    localStorage.setItem('memento_sent_alerts', JSON.stringify(sentAlerts));
  }, [sentAlerts]);


  // 🎯 On calcule les totaux pour les injecter directement dans les boutons
  const countActive = tasks ? tasks.filter((t) => !t.checked).length : 0;
  const countTermine = tasks ? tasks.filter((t) => t.checked).length : 0;
  const countAvecRDV = tasks ? tasks.filter((t) => !t.checked && t.rdvDate && t.rdvDate.trim() !== "" && t.rdvDate.toLowerCase().trim() !== "aucun").length : 0;
  const countSansRDV = tasks ? tasks.filter((t) => !t.checked && (!t.rdvDate || t.rdvDate.trim() === "" || t.rdvDate.toLowerCase().trim() === "aucun")).length : 0;

  // 🎯 2. GÉNÉRATION DE LA LISTE DES BOUTONS DE FILTRES
  const filterList = FILTER_NAMES.map((name) => {
    // On associe le bon chiffre au bon bouton
    let count = 0;
    if (name === 'Toute') count = tasks ? tasks.length : 0;
    if (name === 'Active') count = countActive;
    if (name === 'Terminé') count = countTermine;
    if (name === 'Avec RDV') count = countAvecRDV;
    if (name === 'Sans RDV') count = countSansRDV;

    return (
      <FilterButton
        key={name}
        name={name}
        isPressed={name === filter}
        setFilter={setFilter}
        count={count} // 🎯 On envoie le chiffre au bouton !
      />
    );
  });

  return (
    <React.Fragment>
      <div className="d-flex justify-content-center mb-4 mt-3" style={{ width: '100%' }}>
        <Form.Group style={{ width: '100%', maxWidth: '400px' }}>
          <div className="input-group search-task">
            <span className="input-group-text memento-search-loupe border-secondary border-end-0 text-muted">
              🔍
            </span>
            <Form.Control
              type="text"
              placeholder="Rechercher une tâche par son nom..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ borderRadius: '0 20px 20px 0' }}
              className="text-light border-secondary border-start-0 input-search"
            />
          </div>
        </Form.Group>
      </div>

      <div className='d-flex flex-column justify-content-center mt-4'>
        <div className='d-flex justify-content-center filter-button'>{filterList}</div>
        {/* 🎯 CALCUL DES COMPTEURS EN DIRECT */}
        {(() => {
          if (!tasks) return null;

          // 1. Compte les tâches Actives (non cochées)
          const countActive = tasks.filter((t) => !t.checked).length;

          // 2. Compte les tâches Terminées (cochées)
          const countTermine = tasks.filter((t) => t.checked).length;

          // 3. Compte les tâches Avec RDV (non cochées ET rdvDate valide)
          const countAvecRDV = tasks.filter(
            (t) => !t.checked && t.rdvDate && t.rdvDate.trim() !== "" && t.rdvDate.toLowerCase().trim() !== "aucun"
          ).length;

          // 4. Compte les tâches Sans RDV (non cochées ET rdvDate vide ou "Aucun")
          const countSansRDV = tasks.filter(
            (t) => !t.checked && (!t.rdvDate || t.rdvDate.trim() === "" || t.rdvDate.toLowerCase().trim() === "aucun")
          ).length;

          // Formateur (ex: 5 -> "05")
          const formatNum = (num) => (num < 10 ? `0${num}` : num);

          return (
            <React.Fragment>
              {filter === 'Toute' && (
                <div className='d-flex flex-column align-items-center me-3'>
                  <h4 className="text-info border-bottom border-secondary pb-2 mt-5">
                    Tâches générales <Badge bg="secondary" className={`ms-2 fs-6 badge-fade ${!showBadges ? 'fade-out' : ''}`}>{formatNum(tasks.length)}</Badge>
                  </h4>
                </div>
              )}

              {filter === 'Active' && (
                <div className='d-flex flex-column align-items-center me-3'>
                  <h4 className="text-info border-bottom border-secondary pb-2 mt-5">
                    Tâches actives <Badge bg="info" className={`text-dark ms-2 fs-6 badge-fade ${!showBadges ? 'fade-out' : ''}`}>{formatNum(countActive)}</Badge>
                  </h4>
                </div>
              )}

              {filter === 'Avec RDV' && (
                <div className='d-flex flex-column align-items-center me-3'>
                  <h4 className="text-info border-bottom border-secondary pb-2 mt-5">
                    Tâches avec rendez-vous <Badge bg="warning" text="dark" className={`ms-2 fs-6 badge-fade ${!showBadges ? 'fade-out' : ''}`}>{formatNum(countAvecRDV)}</Badge>
                  </h4>
                </div>
              )}

              {filter === 'Sans RDV' && (
                <div className='d-flex flex-column align-items-center me-3'>
                  <h4 className="text-info border-bottom border-secondary pb-2 mt-5">
                    Tâches sans rendez-vous <Badge bg="light" text="dark" className={`ms-2 fs-6 badge-fade ${!showBadges ? 'fade-out' : ''}`}>{formatNum(countSansRDV)}</Badge>
                  </h4>
                </div>
              )}

              {filter === 'Terminé' && (
                <div className='d-flex flex-column align-items-center me-3'>
                  <h4 className="text-info border-bottom border-secondary pb-2 mt-5">
                    Tâches terminées <Badge bg="success" className={`ms-2 fs-6 badge-fade ${!showBadges ? 'fade-out' : ''}`}>{formatNum(countTermine)}</Badge>
                  </h4>
                </div>
              )}
            </React.Fragment>
          );
        })()}
      </div>

      <Card.Body className='box'>
        {filter !== 'Terminé' && (
          <React.Fragment>
            {(() => {
              if (!tasks) return null;

              // 2. Si la recherche ne donne rien, on affiche le message d'absence de résultat
              if (processedTasks.length === 0) {
                // 🟢 CAS B : L'utilisateur n'a vraiment AUCUNE tâche enregistrée au total pour ce filtre
                const totalTasksForFilter = tasks ? tasks.filter(FILTER_MAP[filter]).length : 0;

                if (totalTasksForFilter === 0) {
                  return (
                    <div className="d-flex flex-column justify-content-center align-items-center text-center text-muted" style={{ minHeight: '200px', width: '100%' }}>
                      <h3 className="text-info mb-3">📝 Liste vide</h3>
                      <p className="fs-6 text-white">Vous n'avez aucune tâche dans cette catégorie.</p>
                    </div>
                  );
                }
                return (
                  <div className="d-flex flex-column justify-content-center align-items-center text-center text-muted" style={{ minHeight: '200px', width: '100%' }} // 🎯 Force le centrage au milieu de la boîte
                  >
                    <h3 className="text-secondary">🔍 Aucun résultat trouvé</h3>
                    <p className="small text-white">Aucune tâche ne correspond à "{searchTerm}"</p>
                  </div>
                );
              }

              // 🎯 2. SI LE FILTRE EST "SANS RDV" : On active le Drag & Drop
              if (filter === 'Sans RDV') {
                return (
                  <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="memento-sans-rdv-list" direction="horizontal">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="d-flex flex-row flex-wrap justify-content-start align-items-stretch draggable-card"
                          style={{ width: '100%', gap: '35px' }}
                        >
                          {(localOrderedTasks.length > 0 ? localOrderedTasks : processedTasks).map((task, index) => {
                            if (task.checked) return null;

                            return (
                              <Draggable key={task.id} draggableId={task.id} index={task.order ?? index}>
                                {(dragProvided) => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                    style={{
                                      ...dragProvided.draggableProps.style,
                                      width: '280px',
                                      marginBottom: '0px', // 🚀 Réduit au minimum pour supprimer l'espace sous la carte
                                      display: 'flex',     // 🚀 Écrase le display: block injecté par la bibliothèque
                                      flexDirection: 'column'
                                    }}
                                  >
                                    <Task
                                      id={task.id}
                                      task={task}
                                      currentTime={currentTime}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                );
              }

              // 3. Sinon, on affiche la liste des tâches normales
              return processedTasks.map((task) => {
                if (!task.rdvDate) return null;

                // 🎯 SÉCURITÉ ABSOLUE : Si la tâche vient d'être cochée, elle disparaît immédiatement
                if (task.checked) return null;

                return (
                  <Task
                    id={task.id}
                    key={task.id}
                    task={task}
                    currentTime={currentTime} // 🎯 Transmet l'horloge réactive
                  />
                );
              });
            })()}
          </React.Fragment>
        )}

        {/* --- SECTION 2 : ACTIONS TERMINÉES --- */}
        {(filter === 'Terminé' || filter === 'Toute') && (
          <React.Fragment>
            {tasks &&
              [...tasks]
                .sort((a, b) => {
                  // Helper local pour convertir "Aucun" ou les chaînes vides en valeur neutre (0)
                  const getValidDateValue = (dateStr) => {
                    if (!dateStr || dateStr.toLowerCase().trim() === 'aucun') return 0;
                    const parsed = new Date(dateStr);
                    return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
                  };

                  // 🎯 TRI DÉCROISSANT (b - a) : Met les rendez-vous les plus récents tout en haut de l'historique
                  return getValidDateValue(b.rdvDate) - getValidDateValue(a.rdvDate);
                })
                .map((task) => {
                  if (!task.rdvDate) return null;

                  // Affiche uniquement les tâches cochées
                  if (!task.checked) return null;

                  return (
                    <Task
                      id={task.id}
                      key={task.id}
                      task={task}
                      currentTime={currentTime} // 🎯 Indispensable ici aussi pour synchroniser l'horloge !
                    />
                  );
                })}
          </React.Fragment>
        )}
      </Card.Body>

      <a
        href="https://paypal.me/GMosson"
        target="_blank"
        rel="noopener noreferrer"
        className="btn-coffee-float"
        title="Soutenir le développement de Memento ☕"
      >
        ☕ Offrez-moi un café
      </a>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => {
  console.log(state);
  const tasks = state.firestore.ordered.tasks;
  return {
    tasks: tasks,
    uid: state.firebase.auth.uid
  };
};

export default compose(
  connect(mapStateToProps),
  firestoreConnect((ownProps) => [
    {
      collection: 'tasks',
      where: [
        'authorId', '==', ownProps.uid || ''
      ],
    }
  ])
)(Tasks);
